import React from 'react';
import { connect } from 'react-redux';
import { Layout, Alert, Skeleton } from 'antd';
import { addDays, addHours, format as formatDate, isSameDay, isAfter } from 'date-fns';
import { Configuration } from '../../types/configuration';
import { User } from '../../types/user';
import { BookingItem } from '../../types/booking';
import DatePicker from '../../components/Datepicker';
import Timepicker from '../../components/Timepicker';

interface Props {
  // HTML attributes
  id?: string;
  emptyState?: boolean;
  selectedDate?: Date;
  skeleton?: boolean;
  isFetchingDate?: boolean;
  isFetchingTime?: boolean;
  onSelectDate: (date: Date) => void;
  selectedTime?: string;
  onSelectTime: (time: string) => void;
  bookingHistory: Record<string, BookingItem[]>;

  users: User[];
  isAdmin: boolean;
  primaryColor: string;
  activeUserId: number;
  bookingSettings: Configuration;
  availableTimes: string[];
  stylistSettings: Record<string, boolean>;
}

class BookingDateTime extends React.PureComponent<Props> {
  private redirect(url: string) {
    const targetWindow = window.parent || window;
    if (targetWindow) {
      targetWindow.location.href = url;
    }
  }

  getActiveUser() {
    const { users, activeUserId } = this.props;
    return users.find(user => user.id === activeUserId);
  }

  shouldDisableDate(date: Date) {
    const { bookingSettings, stylistSettings } = this.props;
    const dayName = formatDate(date, 'dddd');
    const isCustomized = bookingSettings[`canBookOn${dayName}`] !== undefined;

    const { maximumAdvancedBookingDays: maxDays } = bookingSettings;
    const maxDate = maxDays && maxDays > 0 ? addDays(new Date(), maxDays) : null;

    if (isCustomized) {
      if (!bookingSettings[`canBookOn${dayName}`]) {
        return true;
      }

      if (stylistSettings && !stylistSettings[`canBookOn${dayName}`]) {
        return false;
      }
    }

    return maxDate && isAfter(date, maxDate);
  }

  renderEmptyState() {
    return (
      <Alert
        type="warning"
        message="Please select at least one service and stylist to see available date time here!"
      />
    );
  }

  renderContent() {
    const { emptyState, skeleton } = this.props;

    if (emptyState && !skeleton) {
      return this.renderEmptyState();
    }

    const {
      users,
      isAdmin,
      primaryColor,
      bookingHistory,
      bookingSettings,
      selectedDate,
      onSelectDate,
      selectedTime,
      onSelectTime,
      availableTimes,
      isFetchingDate,
      isFetchingTime,
    } = this.props;
    const { minimumAdvancedBookingHours: minHours, maximumAdvancedBookingDays: maxDays } = bookingSettings;
    const minDate = !isAdmin && !!minHours && minHours > 0 ? addHours(new Date(), minHours) : new Date();

    const activeUser = this.getActiveUser();
    let selectedDateBooking;

    if (activeUser && selectedDate) {
      const userBookingHistory = bookingHistory[activeUser.phoneNumber] || [];
      selectedDateBooking = userBookingHistory.find(booking => isSameDay(booking.date, selectedDate));
    }

    return (
      <div className="booking-datetime-container" style={{ paddingBottom: 40, paddingTop: 20 }}>
        <div
          className="booking-datepicker"
          style={{
            marginRight: 'auto',
            marginLeft: 'auto',
            maxWidth: 700,
          }}
        >
          {isFetchingDate && <Skeleton.Input size="large" />}
          {!isFetchingDate && (
            <>
              <DatePicker
                startDate={minDate}
                endDate={maxDays}
                selectDate={selectedDate}
                getSelectedDay={onSelectDate}
                isDisabledDate={date => this.shouldDisableDate(date)}
              />

              {users.length > 1 && (
                <div style={{ color: primaryColor, marginTop: 10, textAlign: 'center', fontWeight: 600 }}>
                  (Selected stylists for other users will reset after changing the date & time)
                </div>
              )}
            </>
          )}
        </div>

        <div
          className="booking-timepicker"
          style={{
            marginRight: 'auto',
            marginLeft: 'auto',
            maxWidth: 700,
          }}
        >
          {isFetchingTime && (
            <div style={{ paddingTop: 30 }}>
              <div style={{ width: '40%', marginBottom: 10 }}>
                <Skeleton.Input size="small" />
              </div>
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(() => (
                <div
                  key={Math.random()}
                  style={{
                    display: 'inline-block',
                    marginRight: 10,
                    marginTop: 10,
                    width: 60,
                  }}
                >
                  <Skeleton.Input size="small" />
                </div>
              ))}
            </div>
          )}

          {!isFetchingTime && selectedDate && (
            <>
              {selectedDateBooking && (
                <div style={{ paddingTop: 30 }}>
                  <Alert
                    type="warning"
                    message={<div>
                      There is an existing booking for this user on <b>{formatDate(selectedDate, 'MMM dd yyyy')}</b> at <span style={{ color: primaryColor, fontWeight: 600 }}>{selectedDateBooking.time}</span>. {isAdmin && selectedDateBooking.link && (
                        <span
                          style={{ cursor: 'pointer', textDecoration: 'underline' }}
                          onClick={() => this.redirect(selectedDateBooking.link)}
                        >View it?</span>
                      )}
                    </div>}
                  />
                </div>
              )}

              {(!selectedDateBooking || isAdmin) && (
                <Timepicker
                  selectedTime={selectedTime}
                  availableTimes={availableTimes}
                  onSelectTime={onSelectTime}
                />
              )}
            </>
          )}
        </div>
      </div>
    );
  }

  render() {
    const { id } = this.props;

    return (
      <Layout
        id={id}
        className="fave-layout fave-block"
        style={{ position: 'static' }}
      >
        <Layout.Header className="fave-block--header">
          Select Date & Time
        </Layout.Header>

        <Layout.Content
          className="fave-block--content"
          style={{
            backgroundColor: '#ffffff',
            minHeight: 160,
          }}
        >
          {this.renderContent()}
        </Layout.Content>
      </Layout>
    );
  }
}

const mapStateToProps = state => ({
  users: state.bookingStore.users,
  isAdmin: state.salonStore.isAdmin,
  primaryColor: state.salonStore.primaryColor,
  activeUserId: state.bookingStore.activeUserId,
  stylistSettings: state.bookingStore.stylistSettings,
  availableTimes: state.bookingStore.availableTimes,
  bookingHistory: state.bookingStore.bookingHistory,
  bookingSettings: state.bookingSettings,
});

export default connect(mapStateToProps)(BookingDateTime);
